import { useMemo } from "react";
import type { ChartTheme } from "@tanstack/charts";
import { useThemeToggle } from "@/hooks/use-theme-toggle";

export type ChartTokens = {
  primary: string;
  success: string;
  destructive: string;
  warning: string;
  muted: string;
  fontFamily: string;
};

export type ChartThemeBundle = {
  theme: ChartTheme;
  tokens: ChartTokens;
};

const LIGHT_TOKENS: ChartTokens = {
  primary: "#1e66f5",
  success: "#16a34a",
  destructive: "#d20f39",
  warning: "#d97706",
  muted: "#6c6f85",
  fontFamily: "'Poppins', 'Poppins Fallback', sans-serif",
};

const DARK_TOKENS: ChartTokens = {
  primary: "#8caaee",
  success: "#34d399",
  destructive: "#e78284",
  warning: "#fbbf24",
  muted: "#a5adce",
  fontFamily: "'Poppins', 'Poppins Fallback', sans-serif",
};

const LIGHT_THEME: ChartTheme = {
  foreground: "#4c4f69",
  muted: "#6c6f85",
  grid: "#bcc0cc",
  background: "#ffffff",
  palette: ["#1e66f5", "#40a02b", "#fe640b", "#d20f39", "#8839ef", "#179299"],
};

const DARK_THEME: ChartTheme = {
  foreground: "#c6d0f5",
  muted: "#a5adce",
  grid: "#51576d",
  background: "#232634",
  palette: ["#8caaee", "#a6d189", "#f9a05b", "#e78284", "#c6a0f6", "#81c8be"],
};

export function useChartTheme(): ChartThemeBundle {
  const { isDark } = useThemeToggle();
  return useMemo(
    () => (isDark ? { theme: DARK_THEME, tokens: DARK_TOKENS } : { theme: LIGHT_THEME, tokens: LIGHT_TOKENS }),
    [isDark],
  );
}
